import React, { useState, useRef } from 'react';
import { Box, Button, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography, Alert, Tabs, Tab } from '@mui/material';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { useTranslation } from 'react-i18next';
import { useCart } from '../context/CartContext';
import client from '../api/client';
import { getCachedProfile } from '../api/users';
import { useNavigate } from 'react-router-dom';

export default function CartPage() {
  const { items, removeItem, updateQuantity, replaceItem, clearCart } = useCart();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [outdatedItems, setOutdatedItems] = useState({});
  const [paymentTab, setPaymentTab] = useState(0);
  const [mockPaypalOrderId, setMockPaypalOrderId] = useState(null);
  const [invoiceSent, setInvoiceSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const orderIdRef = useRef(null);
  const staleCartRef = useRef(false);
  const navigate = useNavigate();

  const total = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const currency = 'EUR';
  const profile = getCachedProfile();
  const profileComplete = profile?.profileComplete === true;
  const { t } = useTranslation();

  const isMock = process.env.REACT_APP_AUTH_MOCK === 'true';
  const invoiceTabIndex = isMock ? 2 : 1;

  async function createOrder() {
    setError('');
    setOutdatedItems({});
    staleCartRef.current = false;
    try {
      const res = await client.post('/orders', {
        items: items.map(i => ({ itemId: i.itemId, title: i.title, price: i.price, quantity: i.quantity })),
        currency,
      });
      orderIdRef.current = res.data.orderId;
      return res.data.paypalOrderId;
    } catch (err) {
      const data = err.response?.data;
      if (data?.error === 'CART_ITEMS_OUTDATED' && Array.isArray(data.outdatedItems)) {
        const map = {};
        for (const entry of data.outdatedItems) {
          map[entry.cartItemId] = entry.replacement ?? null;
        }
        setOutdatedItems(map);
        staleCartRef.current = true;
      } else {
        setError(data?.error || t('cart.createOrderError'));
      }
      throw err;
    }
  }

  async function sendInvoice() {
    setLoading(true);
    setError('');
    setOutdatedItems({});
    try {
      await client.post('/orders', {
        items: items.map(i => ({ itemId: i.itemId, title: i.title, price: i.price, quantity: i.quantity })),
        currency,
        offline: true,
      });
      setInvoiceSent(true);
      clearCart();
    } catch (err) {
      const data = err.response?.data;
      if (data?.error === 'CART_ITEMS_OUTDATED' && Array.isArray(data.outdatedItems)) {
        const map = {};
        for (const entry of data.outdatedItems) {
          map[entry.cartItemId] = entry.replacement ?? null;
        }
        setOutdatedItems(map);
      } else {
        setError(data?.error || t('cart.sendInvoiceError'));
      }
    } finally {
      setLoading(false);
    }
  }

  async function onApprove(data) {
    try {
      await client.post('/payments/capture', {
        orderId: orderIdRef.current,
        paypalOrderId: data.orderID,
      });
      clearCart();
      setSuccess(t('cart.paymentSuccess'));
      setTimeout(() => navigate('/orders'), 1500);
    } catch (err) {
      setError(err.response?.data?.error || t('cart.captureError'));
    }
  }

  function onError(err) {
    if (staleCartRef.current) return;
    setError(err?.message || 'Payment failed. Please try again.');
  }

  if (items.length === 0 && !success && !invoiceSent) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>{t('cart.empty')} <Button onClick={() => navigate('/catalog')}>{t('cart.browseCatalog')}</Button></Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" mb={3}>{t('cart.title')}</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {invoiceSent ? (
        <Alert severity="success" sx={{ mt: 2 }}>{t('cart.invoiceSent')}</Alert>
      ) : (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('cart.item')}</TableCell>
                <TableCell>{t('cart.price')}</TableCell>
                <TableCell>{t('cart.qty')}</TableCell>
                <TableCell>{t('cart.subtotal')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map(item => {
                const isOutdated = item.itemId in outdatedItems;
                const replacement = outdatedItems[item.itemId];
                return (
                  <React.Fragment key={item.itemId}>
                    <TableRow sx={isOutdated ? { outline: '2px solid', outlineColor: 'error.main' } : {}}>
                      <TableCell>{item.title}</TableCell>
                      <TableCell>{item.price} {currency}</TableCell>
                      <TableCell>
                        <TextField type="number" size="small" sx={{ width: 70 }}
                          value={item.quantity}
                          onChange={e => updateQuantity(item.itemId, parseInt(e.target.value, 10))}
                          inputProps={{ min: 1 }} />
                      </TableCell>
                      <TableCell>{(item.price * item.quantity).toFixed(2)}</TableCell>
                      <TableCell>
                        <Button color="error" onClick={() => removeItem(item.itemId)}>{t('cart.remove')}</Button>
                      </TableCell>
                    </TableRow>
                    {isOutdated && (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ pt: 0, pb: 1 }}>
                          <Alert severity="error" sx={{ mb: replacement ? 1 : 0 }}
                            action={replacement ? (
                              <Button color="inherit" size="small" onClick={() => {
                                replaceItem(item.itemId, replacement);
                                setOutdatedItems(prev => {
                                  const next = { ...prev };
                                  delete next[item.itemId];
                                  return next;
                                });
                              }}>
                                {t('cart.updateToLatest', { price: replacement.price, currency })}
                              </Button>
                            ) : undefined}
                          >
                            {replacement
                              ? t('cart.itemUpdated', { title: replacement.title })
                              : t('cart.itemUnavailable')}
                          </Alert>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, mt: 3 }}>
            <Typography variant="h6">{t('cart.total')} {total.toFixed(2)} {currency}</Typography>
          </Box>
          <Box sx={{ mt: 3 }}>
            <Tabs value={paymentTab} onChange={(_, v) => setPaymentTab(v)}>
              <Tab label={t('cart.paypal')} />
              {isMock && <Tab label={t('cart.paypalMock')} />}
              <Tab label={t('cart.sendInvoice')} />
            </Tabs>
            <Box sx={{ mt: 2 }}>
              {paymentTab === 0 && (
                profileComplete ? (
                  <PayPalButtons
                    createOrder={createOrder}
                    onApprove={onApprove}
                    onError={onError}
                  />
                ) : (
                  <Alert severity="warning" dangerouslySetInnerHTML={{ __html: t('cart.completeProfile') }} />
                )
              )}
              {isMock && paymentTab === 1 && (
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <Button
                    variant="outlined"
                    disabled={!!mockPaypalOrderId || loading}
                    onClick={async () => {
                      setLoading(true);
                      try {
                        const paypalOrderId = await createOrder();
                        setMockPaypalOrderId(paypalOrderId);
                      } finally {
                        setLoading(false);
                      }
                    }}
                  >
                    {loading ? t('cart.creatingOrder') : t('cart.createOrder')}
                  </Button>
                  <Button
                    variant="contained"
                    disabled={!mockPaypalOrderId || loading}
                    onClick={async () => {
                      setLoading(true);
                      try {
                        await onApprove({ orderID: mockPaypalOrderId });
                        setMockPaypalOrderId(null);
                      } finally {
                        setLoading(false);
                      }
                    }}
                  >
                    {loading ? t('cart.capturing') : t('cart.capture')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      setMockPaypalOrderId(null);
                      orderIdRef.current = null;
                      onError(new Error('Mock payment failure'));
                    }}
                  >
                    {t('cart.fail')}
                  </Button>
                </Box>
              )}
              {paymentTab === invoiceTabIndex && (
                profileComplete ? (
                  <Button variant="contained" onClick={sendInvoice} disabled={loading}>
                    {loading ? t('cart.sendingInvoice') : t('cart.sendInvoiceBtn')}
                  </Button>
                ) : (
                  <Alert severity="warning" dangerouslySetInnerHTML={{ __html: t('cart.completeProfile') }} />
                )
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}
